import { useQuery } from '@tanstack/react-query'
import { UNIFIED_CHAINS_SUMMARY_KEY } from 'constants/tableQueryKeys'
import { useMemo } from 'react'
import useApiService from 'services/api'
import { getTargetType } from './targetTypeMap'
import {
  adaptUnifiedItemForVertical,
  adaptUnifiedListParams,
  VerticalRowConfig,
} from './unifiedResponseAdapter'

/**
 * Restaurant (ChainsTable) unified data source (FIR-6995, default-on since the
 * FIR-6997 cutover).
 *
 * ChainsTable is the one opps table that does NOT use `DECommonTable` — it has a
 * bespoke component (legacy + reports V2) with elastic search, a product
 * selector, and a rich summary header. So instead of the generic
 * `useUnifiedTableActions` hook, this returns the pieces the bespoke component
 * needs to read from the unified data source:
 *
 *  - `listAction`: a bound list fetcher with a
 *    `(params, signal) => Promise<{ results, count }>` shape, with each row
 *    re-nested under `chain` (and `contact_company_id` / `overriden_name` set)
 *    so the existing chain columns, detail links, and row actions resolve.
 *  - `unifiedCount`: the unified summary count for the current filters, to drive
 *    pagination + `setTotalRowsInBackend` so paging matches the unified rows.
 */

// ChainsTable columns nest under `chain` and read `chain.id` for the
// `/chain/...` detail link; row actions key off `contact_company_id`.
const RESTAURANT_ROW_CONFIG: VerticalRowConfig = {
  nestKey: 'chain',
  companyIdAliases: ['contact_company_id'],
}

export function useUnifiedChainsTable(filterParams: Record<string, any>): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: any[]; count: number }>
  unifiedCount: number | undefined
  unifiedPlacesCount: number | undefined
  unifiedLtv: number | undefined
  unifiedSummaryFetching: boolean
} {
  const api = useApiService()

  const listAction = useMemo(() => {
    const targetType = getTargetType('restaurant')
    return async (params: Record<string, any>, signal?: AbortSignal) => {
      const res = await api.getOpportunitiesList(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      return {
        ...res,
        results: res.results.map((item) =>
          adaptUnifiedItemForVertical(
            item as Record<string, unknown>,
            RESTAURANT_ROW_CONFIG
          )
        ),
      } as { results: any[]; count: number }
    }
  }, [api])

  const { data: unifiedSummary, isFetching: unifiedSummaryFetching } = useQuery(
    {
      // Use the default (finite, 0) staleTime so the count refetches after
      // mutations that invalidate UNIFIED_CHAINS_SUMMARY_KEY. Do NOT set
      // `staleTime: Infinity` here: an infinite staleTime would leave the
      // count stale until a hard reload.
      queryKey: [UNIFIED_CHAINS_SUMMARY_KEY, filterParams],
      queryFn: async ({ signal }) =>
        api.getOpportunitiesSummary(
          getTargetType('restaurant'),
          adaptUnifiedListParams(filterParams, getTargetType('restaurant')),
          signal
        ),
    }
  )

  return {
    listAction,
    // The full stat-card set for the filtered chains, all from the unified opps
    // summary. `places_count` = summed open doors of the matching chains
    // ("Total Doors"); `total_ltv` is product-aware.
    unifiedCount: unifiedSummary?.count,
    unifiedPlacesCount: unifiedSummary?.places_count,
    unifiedLtv: unifiedSummary?.total_ltv,
    unifiedSummaryFetching,
  }
}
